import type { DropPointInput, LooseCargoDetailInput } from '@chukua/common';
import { useState } from 'react';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { addDropPoint, removeDropPoint, reorderDropPoints, saveJobLooseCargoDetails, updateDropPoint, type DropPoint } from '../../../jobs.api';

type Toast = ReturnType<typeof useToast>;

async function rowAction(action: () => Promise<unknown>, onChanged: () => Promise<void>, toast: Toast, successMessage: string, errorMessage: string) {
  try {
    await action();
    await onChanged();
    toast.success(successMessage);
    return true;
  } catch {
    toast.error(errorMessage);
    return false;
  }
}

/**
 * Add, edit, remove and reorder a job's drop points, and save its loose-cargo details —
 * `onChanged` (the job page's own `reload`) is how the caller sees the result, same idiom as
 * `useJobContainers`. Add/edit/remove toast (CLAUDE.md → Feedback), like `useJobContainers`'s
 * row actions; a reorder is closer to sorting a list than creating or deleting a row, so it
 * stays silent on success and only toasts its failure; the details form is a page-level save,
 * so its failure stays inline.
 */
export function useLooseCargo(jobId: string, onChanged: () => Promise<void>) {
  const [savingDropPoint, setSavingDropPoint] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [savingDetails, setSavingDetails] = useState(false);
  const toast = useToast();

  function withDropPointSaving(fn: () => Promise<boolean>): Promise<boolean> {
    setSavingDropPoint(true);
    return fn().finally(() => setSavingDropPoint(false));
  }

  const add = (input: DropPointInput) =>
    withDropPointSaving(() =>
      rowAction(() => addDropPoint(jobId, input), onChanged, toast, `Drop point ${input.contactName} added`, 'Could not add drop point'),
    );

  const update = (dropPointId: string, input: DropPointInput) =>
    withDropPointSaving(() =>
      rowAction(() => updateDropPoint(jobId, dropPointId, input), onChanged, toast, 'Drop point updated', 'Could not update drop point'),
    );

  const remove = (dropPointId: string) =>
    rowAction(() => removeDropPoint(jobId, dropPointId), onChanged, toast, 'Drop point removed', 'Could not remove drop point');

  const reorder = (dropPoints: DropPoint[]) =>
    withDropPointSaving(async () => {
      try {
        await reorderDropPoints(jobId, { dropPoints: dropPoints.map((d, order) => ({ id: d.id, order })) });
        await onChanged();
        return true;
      } catch {
        toast.error('Could not reorder drop points');
        return false;
      }
    });

  async function saveDetails(input: LooseCargoDetailInput): Promise<boolean> {
    setDetailsError(null);
    setSavingDetails(true);
    try {
      await saveJobLooseCargoDetails(jobId, input);
      await onChanged();
      toast.success('Loose cargo details saved');
      return true;
    } catch {
      setDetailsError('Could not save — check the fields and try again.');
      return false;
    } finally {
      setSavingDetails(false);
    }
  }

  return { savingDropPoint, add, update, remove, reorder, detailsError, savingDetails, saveDetails };
}
